/* ═══════════════════════════════════════════════════════════════
   The Designer — the Navigation palette.

   Word's navigation pane, and the same thing the master editors show:
   the heading map of the section that is open. It reads the LIVE
   ProseMirror document and the ONE numbering brain, so a heading that
   appears here appears on the paper with exactly that number. Clicking
   a row jumps the canvas to it and puts the caret there — navigating is
   never a separate mode from writing.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import type { ContentBlock, StandardConfig } from '../../shared/types';
import { computeNumbering } from '../../shared/body-layout';
import EmptyState from '../components/EmptyState';

export interface NavItem {
  id: string;
  pos: number;
  level: number;
  prefix: string;
  text: string;
}

/** Levels 1–3 are the section's skeleton — the default map. */
const SHALLOW_DEPTH = 3;

const NavIcon = (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 6h16M4 12h11M4 18h7" />
  </svg>
);

/** Read the heading map out of the editor's current document. */
export function readNavItems(editor: Editor | null, forma: StandardConfig | null): NavItem[] {
  if (!editor) return [];
  const blocks: ContentBlock[] = [];
  const positions: number[] = [];
  const texts: string[] = [];
  editor.state.doc.forEach((node, offset) => {
    blocks.push({
      id: (node.attrs.blockId as string) || String(offset),
      level: (node.attrs.level as number) || 1,
      text: '',
      continuation: !!node.attrs.continuation,
      excluded: !!node.attrs.excluded,
    });
    positions.push(offset);
    texts.push(node.textContent || (node.attrs.text as string) || '');
  });
  const refs = computeNumbering(blocks, forma);
  return blocks.map((b, i) => ({
    id: b.id,
    pos: positions[i],
    level: b.level,
    prefix: refs[i]?.prefix || '',
    text: texts[i],
  }));
}

export function NavigationPalette({
  editor,
  forma,
  onJump,
}: {
  editor: Editor | null;
  forma: StandardConfig | null;
  /** Scroll the canvas to a block and place the caret in it. */
  onJump: (item: NavItem) => void;
}) {
  const [query, setQuery] = React.useState('');
  const [allLevels, setAllLevels] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);

  // The map follows the document: typing a new heading makes it appear.
  const [version, bump] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    const onUpdate = () => bump();
    editor.on('update', onUpdate);
    return () => {
      editor.off('update', onUpdate);
    };
  }, [editor]);

  const items = React.useMemo(
    () => readNavItems(editor, forma),
    // `version` is the document's revision — it is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, forma, version]
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (!allLevels && it.level > SHALLOW_DEPTH) return false;
      if (!q) return true;
      return it.text.toLowerCase().includes(q) || it.prefix.toLowerCase().includes(q);
    });
  }, [items, query, allLevels]);

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[data-nav-item]') || []
    );
    const idx = rows.findIndex((el) => el === document.activeElement);
    const next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
    rows[Math.max(0, Math.min(rows.length - 1, next))]?.focus();
  };

  return (
    <div className="dsg-pal dsg-nav">
      <div className="dsg-pal-controls">
        <label className="sr-only" htmlFor="dsg-nav-filter">
          Filter headings
        </label>
        <input
          id="dsg-nav-filter"
          type="search"
          className="dsg-input"
          placeholder="Find a heading"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button
          type="button"
          className={`dsg-btn dsg-btn-sm${allLevels ? ' is-on' : ''}`}
          aria-pressed={allLevels}
          onClick={() => setAllLevels((v) => !v)}
          title={
            allLevels
              ? 'Show only the section skeleton (levels 1–3)'
              : 'Show every paragraph, all nine levels'
          }
        >
          {allLevels ? 'Levels 1–3' : 'All levels'}
        </button>
      </div>

      <div
        ref={listRef}
        className="dsg-nav-list"
        role="listbox"
        aria-label="Headings in this section"
        onKeyDown={onListKeyDown}
      >
        {filtered.length === 0 ? (
          <div className="dsg-nav-empty">
            <EmptyState
              icon={NavIcon}
              title={items.length ? 'Nothing matches that' : 'Nothing to navigate yet'}
              subtitle={
                items.length
                  ? 'Clear the filter, or switch to all levels.'
                  : 'Headings appear here as you write them.'
              }
            />
          </div>
        ) : (
          filtered.map((it) => (
            <button
              key={it.id}
              type="button"
              data-nav-item
              role="option"
              aria-selected={false}
              className={`dsg-nav-item dsg-nav-l${Math.min(it.level, 4)}`}
              onClick={() => onJump(it)}
              title={it.text}
            >
              {it.prefix && <span className="dsg-nav-prefix">{it.prefix}</span>}
              <span className="dsg-nav-text">{it.text || <em>Empty paragraph</em>}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
